import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Eye, EyeOff, Link, Key, CopyCheck } from 'lucide-react';

interface ShareLinkProps {
  url: string;
  password?: string;
  onCopy: () => void;
}

export default function ShareLink({ url, password, onCopy }: ShareLinkProps) {
  const { t } = useTranslation('shareNote');
  const [showPassword, setShowPassword] = useState(false);
  const [copiedState, setCopiedState] = useState<'none' | 'link' | 'password' | 'both'>('none');

  // Get the base URL of the current site
  const baseUrl = window.location.origin;

  // Create the full URL using the base URL and the path
  const fullUrl = `${baseUrl}${url}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedState('link');
      onCopy();
      setTimeout(() => setCopiedState('none'), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCopyPassword = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopiedState('password');
      onCopy();
      setTimeout(() => setCopiedState('none'), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const handleCopyBoth = async () => {
    if (!password) return;
    try {
      const text = `${t('share.url')}: ${fullUrl}\n${t('share.password')}: ${password}`;
      await navigator.clipboard.writeText(text);
      setCopiedState('both');
      onCopy();
      setTimeout(() => setCopiedState('none'), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Link Section */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('share.url')}
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={fullUrl}
            readOnly
            className="flex-1 bg-transparent border-none focus:outline-none text-gray-800"
          />
          <button
            onClick={handleCopyLink}
            className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
            title={t('actions.copyLink')}
          >
            {copiedState === 'link' ? (
              <CopyCheck className="w-5 h-5 text-green-500" />
            ) : (
              <Link className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Password Section */}
      {password && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('share.password')}
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center bg-white rounded-lg border border-gray-200">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                readOnly
                className="flex-1 px-3 py-2 bg-transparent border-none focus:outline-none text-gray-800"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                title={showPassword ? t('actions.hidePassword') : t('actions.showPassword')}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            <button
              onClick={handleCopyPassword}
              className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
              title={t('actions.copyPassword')}
            >
              {copiedState === 'password' ? (
                <CopyCheck className="w-5 h-5 text-green-500" />
              ) : (
                <Key className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Copy Both Button */}
      {password && (
        <button
          onClick={handleCopyBoth}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            copiedState === 'both'
              ? 'bg-green-500 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          {copiedState === 'both' ? (
            <>
              <CopyCheck className="w-5 h-5" />
              {t('actions.copied')}
            </>
          ) : (
            <>
              <Copy className="w-5 h-5" />
              {t('actions.copyBoth')}
            </>
          )}
        </button>
      )}
    </div>
  );
}